import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllStudyBuddies } from "../../../services/api";

const StudyBuddiesModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    totalMembers: 0,
    totalUniqueBuddies: 0,
    groupsCount: 0,
    memberships: [],
    uniqueBuddies: [],
    groups: [],
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("all"); // 'all' | 'buddy' | 'group'
  const [selectedGroupFilter, setSelectedGroupFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all"); // 'all' | 'admin' | 'member'
  const [copiedEmail, setCopiedEmail] = useState("");

  // Load buddies data when modal opens
  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;
    const fetchBuddies = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getAllStudyBuddies();
        if (isMounted && res) {
          setData({
            totalMembers: res.totalMembers || 0,
            totalUniqueBuddies: res.totalUniqueBuddies || 0,
            groupsCount: res.groupsCount || 0,
            memberships: Array.isArray(res.memberships) ? res.memberships : [],
            uniqueBuddies: Array.isArray(res.uniqueBuddies) ? res.uniqueBuddies : [],
            groups: Array.isArray(res.groups) ? res.groups : [],
          });
        }
      } catch (err) {
        if (isMounted) {
          setError(err?.response?.data?.message || "Failed to load study buddies.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchBuddies();
    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  // Color mapper helper for group tags
  const getColorStyles = (colorName = "blue") => {
    const c = String(colorName).toLowerCase();
    if (c === "purple") {
      return {
        bg: "bg-purple-50",
        text: "text-purple-700",
        border: "border-purple-200",
        badge: "bg-purple-100/70 text-purple-800",
      };
    }
    if (c === "green") {
      return {
        bg: "bg-emerald-50",
        text: "text-emerald-700",
        border: "border-emerald-200",
        badge: "bg-emerald-100/70 text-emerald-800",
      };
    }
    if (c === "orange") {
      return {
        bg: "bg-amber-50",
        text: "text-amber-800",
        border: "border-amber-200",
        badge: "bg-amber-100/70 text-amber-800",
      };
    }
    return {
      bg: "bg-blue-50",
      text: "text-blue-700",
      border: "border-blue-200",
      badge: "bg-blue-100/70 text-blue-800",
    };
  };

  const handleCopyEmail = async (email, e) => {
    if (e) e.stopPropagation();
    if (!email) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(email);
        setCopiedEmail(email);
        setTimeout(() => setCopiedEmail(""), 2000);
      }
    } catch {
      // no-op
    }
  };

  const handleGoToGroup = (groupId) => {
    if (!groupId) return;
    onClose();
    navigate(`/study-group/${groupId}/hub`);
  };

  // Filtered memberships ('all' view)
  const filteredMemberships = useMemo(() => {
    let list = data.memberships || [];

    // Group filter
    if (selectedGroupFilter !== "all") {
      list = list.filter((m) => String(m.group?.id) === String(selectedGroupFilter));
    }

    // Role filter
    if (roleFilter === "admin") {
      list = list.filter((m) => m.isOwner);
    } else if (roleFilter === "member") {
      list = list.filter((m) => !m.isOwner);
    }

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (m) =>
          (m.fullName || "").toLowerCase().includes(q) ||
          (m.email || "").toLowerCase().includes(q) ||
          (m.course || "").toLowerCase().includes(q) ||
          (m.group?.name || "").toLowerCase().includes(q) ||
          (m.group?.subject || "").toLowerCase().includes(q)
      );
    }

    return list;
  }, [data.memberships, selectedGroupFilter, roleFilter, searchQuery]);

  // Filtered unique buddies ('buddy' view)
  const filteredUniqueBuddies = useMemo(() => {
    let list = data.uniqueBuddies || [];

    // Group filter
    if (selectedGroupFilter !== "all") {
      list = list.filter((b) =>
        (b.groups || []).some((g) => String(g.id) === String(selectedGroupFilter))
      );
    }

    // Role filter
    if (roleFilter === "admin") {
      list = list.filter((b) => (b.groups || []).some((g) => g.isOwner));
    } else if (roleFilter === "member") {
      list = list.filter((b) => (b.groups || []).some((g) => !g.isOwner));
    }

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (b) =>
          (b.fullName || "").toLowerCase().includes(q) ||
          (b.email || "").toLowerCase().includes(q) ||
          (b.course || "").toLowerCase().includes(q) ||
          (b.groups || []).some((g) =>
            (g.name || "").toLowerCase().includes(q) ||
            (g.subject || "").toLowerCase().includes(q)
          )
      );
    }

    return list;
  }, [data.uniqueBuddies, selectedGroupFilter, roleFilter, searchQuery]);

  // Grouped members ('group' view)
  const groupedData = useMemo(() => {
    const list = filteredMemberships;
    const map = {};

    list.forEach((m) => {
      const gId = String(m.group?.id);
      if (!map[gId]) {
        map[gId] = {
          group: m.group,
          members: [],
        };
      }
      map[gId].members.push(m);
    });

    return Object.values(map);
  }, [filteredMemberships]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[120] flex items-center justify-center p-3 sm:p-5 backdrop-blur-sm animate-in fade-in duration-200 font-sans">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl h-[90vh] max-h-[820px] overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col border border-gray-100">
        
        {/* ========================================================================= */}
        {/* MODAL HEADER */}
        {/* ========================================================================= */}
        <div className="px-5 sm:px-7 py-4 sm:py-5 border-b border-gray-100 flex items-center justify-between bg-white shrink-0">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-lg shrink-0 shadow-sm border border-emerald-100">
              <i className="fa-solid fa-user-group"></i>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg sm:text-xl font-extrabold text-gray-900 tracking-tight">
                  Study Buddies
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100/70 text-emerald-800 border border-emerald-200">
                  {data.totalMembers} Total
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                All connected members across your study circles
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition flex items-center justify-center"
              aria-label="Close modal"
            >
              <i className="fa-solid fa-xmark text-base"></i>
            </button>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* STATS HIGHLIGHT STRIP */}
        {/* ========================================================================= */}
        <div className="bg-slate-50/70 px-5 sm:px-7 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-white border border-gray-200/80 font-medium text-gray-700 shadow-xs">
              <i className="fa-solid fa-users text-emerald-600 text-[11px]"></i>
              <span>Total Memberships:</span>
              <strong className="text-gray-900 font-bold">{data.totalMembers}</strong>
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-white border border-gray-200/80 font-medium text-gray-700 shadow-xs">
              <i className="fa-solid fa-user-check text-blue-600 text-[11px]"></i>
              <span>Unique Peers:</span>
              <strong className="text-gray-900 font-bold">{data.totalUniqueBuddies}</strong>
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-white border border-gray-200/80 font-medium text-gray-700 shadow-xs">
              <i className="fa-solid fa-layer-group text-purple-600 text-[11px]"></i>
              <span>Study Circles:</span>
              <strong className="text-gray-900 font-bold">{data.groupsCount}</strong>
            </span>
          </div>

          {/* View Mode Pill Toggle */}
          <div className="flex items-center gap-1 p-1 bg-white rounded-xl border border-gray-200 shadow-xs">
            <button
              type="button"
              onClick={() => setViewMode("all")}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition ${
                viewMode === "all"
                  ? "bg-blue-600 text-white shadow-xs"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              All ({data.totalMembers})
            </button>
            <button
              type="button"
              onClick={() => setViewMode("buddy")}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition ${
                viewMode === "buddy"
                  ? "bg-blue-600 text-white shadow-xs"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              Unique ({data.totalUniqueBuddies})
            </button>
            <button
              type="button"
              onClick={() => setViewMode("group")}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition ${
                viewMode === "group"
                  ? "bg-blue-600 text-white shadow-xs"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              By Group
            </button>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* CONTROLS TOOLBAR: SEARCH & FILTERS */}
        {/* ========================================================================= */}
        <div className="px-5 sm:px-7 py-3 bg-white border-b border-gray-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 shrink-0">
          {/* Search Bar */}
          <div className="relative flex-1">
            <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by student name, email, or study group..."
              className="w-full pl-9 pr-8 py-2 rounded-xl text-xs bg-gray-50 border border-gray-200 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition font-medium text-gray-800"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Group Filter Dropdown */}
            {data.groups.length > 0 && (
              <div className="relative flex-1 sm:flex-initial">
                <select
                  value={selectedGroupFilter}
                  onChange={(e) => setSelectedGroupFilter(e.target.value)}
                  className="w-full sm:w-auto px-3 py-2 pr-7 rounded-xl text-xs font-semibold bg-gray-50 border border-gray-200 text-gray-700 outline-none focus:border-blue-500 transition appearance-none cursor-pointer"
                >
                  <option value="all">All Circles ({data.groups.length})</option>
                  {data.groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name} ({g.memberCount})
                    </option>
                  ))}
                </select>
                <i className="fa-solid fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none"></i>
              </div>
            )}

            {/* Role Filter */}
            <div className="relative flex-1 sm:flex-initial">
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full sm:w-auto px-3 py-2 pr-7 rounded-xl text-xs font-semibold bg-gray-50 border border-gray-200 text-gray-700 outline-none focus:border-blue-500 transition appearance-none cursor-pointer"
              >
                <option value="all">All Roles</option>
                <option value="admin">Group Admins 👑</option>
                <option value="member">Classmates</option>
              </select>
              <i className="fa-solid fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none"></i>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* BUDDIES CONTENT LIST */}
        {/* ========================================================================= */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-7 custom-scrollbar bg-slate-50/40">
          
          {/* LOADING SKELETON */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs animate-pulse flex items-center gap-3.5"
                >
                  <div className="w-12 h-12 rounded-full bg-gray-200 shrink-0"></div>
                  <div className="flex-1 space-y-2">
                    <div className="w-3/4 h-4 bg-gray-200 rounded"></div>
                    <div className="w-1/2 h-3 bg-gray-200 rounded"></div>
                    <div className="w-1/3 h-2.5 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            /* ERROR STATE */
            <div className="bg-white rounded-3xl p-8 border border-red-100 text-center max-w-md mx-auto my-8 shadow-xs">
              <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center text-xl mx-auto mb-3">
                <i className="fa-solid fa-triangle-exclamation"></i>
              </div>
              <h4 className="text-base font-bold text-gray-900 mb-1">Failed to Load Buddies</h4>
              <p className="text-xs text-gray-500 mb-4">{error}</p>
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  getAllStudyBuddies()
                    .then((res) => {
                      if (res) setData(res);
                    })
                    .catch((err) => setError(err?.message || "Failed"))
                    .finally(() => setLoading(false));
                }}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white transition shadow-sm"
              >
                Try Again
              </button>
            </div>
          ) : viewMode === "all" ? (
            /* =================================================================== */
            /* VIEW MODE: ALL MEMBERSHIPS (Default - shows all 16 members)        */
            /* =================================================================== */
            filteredMemberships.length === 0 ? (
              <div className="bg-white rounded-3xl p-10 border border-gray-100 text-center max-w-md mx-auto my-6 shadow-xs">
                <div className="w-12 h-12 rounded-2xl bg-gray-100 text-gray-400 flex items-center justify-center text-xl mx-auto mb-3">
                  <i className="fa-solid fa-user-slash"></i>
                </div>
                <h4 className="text-base font-bold text-gray-800 mb-1">No Study Buddies Found</h4>
                <p className="text-xs text-gray-500 mb-4">
                  {searchQuery || selectedGroupFilter !== "all" || roleFilter !== "all"
                    ? "Try adjusting your filters or search terms."
                    : "You haven't added any study buddies to your groups yet."}
                </p>
                {(searchQuery || selectedGroupFilter !== "all" || roleFilter !== "all") && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedGroupFilter("all");
                      setRoleFilter("all");
                    }}
                    className="px-4 py-2 rounded-xl text-xs font-bold bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
                  >
                    Reset Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {filteredMemberships.map((m) => {
                  const colorStyles = getColorStyles(m.group?.color);
                  const isCurrentCopied = copiedEmail === m.email;

                  return (
                    <div
                      key={m.membershipId}
                      className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between gap-3 group"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <img
                            src={
                              m.avatar ||
                              `https://api.dicebear.com/6.x/initials/svg?seed=${encodeURIComponent(
                                m.fullName || "Student"
                              )}`
                            }
                            alt={m.fullName}
                            className="w-11 h-11 rounded-full bg-slate-100 border border-gray-200 object-cover shrink-0 shadow-xs"
                          />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="text-sm font-bold text-gray-900 truncate">
                                {m.fullName}
                              </p>
                              {m.isCurrentUser && (
                                <span className="px-1.5 py-0.2 rounded-md bg-blue-100 text-blue-700 text-[10px] font-bold">
                                  You
                                </span>
                              )}
                              {m.isOwner ? (
                                <span className="px-1.5 py-0.2 rounded-md bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold">
                                  👑 Admin
                                </span>
                              ) : (
                                <span className="px-1.5 py-0.2 rounded-md bg-gray-100 text-gray-600 text-[10px] font-medium">
                                  Peer
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 truncate mt-0.5">
                              {m.email || (m.course ? `${m.course} student` : "Study Buddy")}
                            </p>
                          </div>
                        </div>

                        {/* Quick Email Copy */}
                        {m.email && (
                          <button
                            type="button"
                            onClick={(e) => handleCopyEmail(m.email, e)}
                            className="text-gray-300 hover:text-blue-600 transition p-1 text-xs shrink-0"
                            title={isCurrentCopied ? "Email copied!" : `Copy ${m.email}`}
                          >
                            <i
                              className={`fa-solid ${
                                isCurrentCopied ? "fa-check text-emerald-500" : "fa-copy"
                              }`}
                            ></i>
                          </button>
                        )}
                      </div>

                      {/* Group association strip & Enter Hub Action */}
                      <div className="pt-2.5 border-t border-gray-50 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.8 rounded-lg text-[11px] font-bold ${colorStyles.bg} ${colorStyles.text} ${colorStyles.border} border truncate`}
                            title={`In group: ${m.group?.name}`}
                          >
                            <i className="fa-solid fa-users text-[9px]"></i>
                            <span className="truncate">{m.group?.name}</span>
                          </span>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleGoToGroup(m.group?.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition shrink-0"
                          title="Open this group hub"
                        >
                          <span>Group Hub</span>
                          <i className="fa-solid fa-arrow-right text-[9px]"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : viewMode === "buddy" ? (
            /* =================================================================== */
            /* VIEW MODE: UNIQUE BUDDIES (Deduplicated with all shared groups)     */
            /* =================================================================== */
            filteredUniqueBuddies.length === 0 ? (
              <div className="bg-white rounded-3xl p-10 border border-gray-100 text-center max-w-md mx-auto my-6 shadow-xs">
                <div className="w-12 h-12 rounded-2xl bg-gray-100 text-gray-400 flex items-center justify-center text-xl mx-auto mb-3">
                  <i className="fa-solid fa-user-slash"></i>
                </div>
                <h4 className="text-base font-bold text-gray-800 mb-1">No Unique Buddies Found</h4>
                <p className="text-xs text-gray-500 mb-4">No results matched your search criteria.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {filteredUniqueBuddies.map((b) => {
                  const isCurrentCopied = copiedEmail === b.email;

                  return (
                    <div
                      key={b.userId}
                      className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between gap-3 group"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <img
                            src={
                              b.avatar ||
                              `https://api.dicebear.com/6.x/initials/svg?seed=${encodeURIComponent(
                                b.fullName || "Student"
                              )}`
                            }
                            alt={b.fullName}
                            className="w-11 h-11 rounded-full bg-slate-100 border border-gray-200 object-cover shrink-0 shadow-xs"
                          />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="text-sm font-bold text-gray-900 truncate">
                                {b.fullName}
                              </p>
                              {b.isCurrentUser && (
                                <span className="px-1.5 py-0.2 rounded-md bg-blue-100 text-blue-700 text-[10px] font-bold">
                                  You
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 truncate mt-0.5">
                              {b.email || (b.course ? `${b.course} student` : "Study Buddy")}
                            </p>
                          </div>
                        </div>

                        {b.email && (
                          <button
                            type="button"
                            onClick={(e) => handleCopyEmail(b.email, e)}
                            className="text-gray-300 hover:text-blue-600 transition p-1 text-xs shrink-0"
                            title={isCurrentCopied ? "Email copied!" : `Copy ${b.email}`}
                          >
                            <i
                              className={`fa-solid ${
                                isCurrentCopied ? "fa-check text-emerald-500" : "fa-copy"
                              }`}
                            ></i>
                          </button>
                        )}
                      </div>

                      {/* List of groups shared */}
                      <div className="pt-2 border-t border-gray-50">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                          In {b.groups?.length || 0} Circle{b.groups?.length > 1 ? "s" : ""}:
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {(b.groups || []).map((grp) => {
                            const cStyle = getColorStyles(grp.color);
                            return (
                              <button
                                key={grp.id}
                                type="button"
                                onClick={() => handleGoToGroup(grp.id)}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold ${cStyle.bg} ${cStyle.text} ${cStyle.border} border hover:opacity-85 transition`}
                                title={`Click to enter ${grp.name}`}
                              >
                                <span>{grp.name}</span>
                                {grp.isOwner && <span>👑</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            /* =================================================================== */
            /* VIEW MODE: BY GROUP (Organized by circle)                          */
            /* =================================================================== */
            groupedData.length === 0 ? (
              <div className="bg-white rounded-3xl p-10 border border-gray-100 text-center max-w-md mx-auto my-6 shadow-xs">
                <div className="w-12 h-12 rounded-2xl bg-gray-100 text-gray-400 flex items-center justify-center text-xl mx-auto mb-3">
                  <i className="fa-solid fa-layer-group"></i>
                </div>
                <h4 className="text-base font-bold text-gray-800 mb-1">No Study Groups Found</h4>
                <p className="text-xs text-gray-500 mb-4">No groups matched the active filter.</p>
              </div>
            ) : (
              <div className="space-y-5">
                {groupedData.map((section) => {
                  const grp = section.group;
                  const cStyle = getColorStyles(grp?.color);

                  return (
                    <div
                      key={grp?.id}
                      className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden shadow-xs"
                    >
                      {/* Group Header */}
                      <div
                        className={`p-3.5 sm:p-4 ${cStyle.bg} border-b ${cStyle.border} flex items-center justify-between gap-3`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-8 h-8 rounded-xl bg-white shadow-xs flex items-center justify-center text-xs shrink-0">
                            <i className={`fa-solid fa-users ${cStyle.text}`}></i>
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-sm font-bold text-gray-900 truncate">
                              {grp?.name}
                            </h4>
                            <p className="text-[11px] text-gray-500 truncate">
                              {grp?.subject} • {section.members.length} Member{section.members.length > 1 ? "s" : ""}
                            </p>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleGoToGroup(grp?.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-white text-blue-700 hover:bg-blue-50 border border-gray-200 shadow-xs transition shrink-0"
                        >
                          <span>Enter Hub</span>
                          <i className="fa-solid fa-arrow-right text-[10px]"></i>
                        </button>
                      </div>

                      {/* Group Member items */}
                      <div className="p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {section.members.map((m) => (
                          <div
                            key={m.membershipId}
                            className="flex items-center justify-between gap-2.5 p-2.5 rounded-xl bg-gray-50/70 border border-gray-100 hover:bg-white hover:border-gray-200 transition"
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <img
                                src={
                                  m.avatar ||
                                  `https://api.dicebear.com/6.x/initials/svg?seed=${encodeURIComponent(
                                    m.fullName || "Student"
                                  )}`
                                }
                                alt={m.fullName}
                                className="w-9 h-9 rounded-full bg-white border border-gray-200 object-cover shrink-0"
                              />
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <p className="text-xs font-bold text-gray-900 truncate">
                                    {m.fullName}
                                  </p>
                                  {m.isCurrentUser && (
                                    <span className="text-[9px] font-bold text-blue-700 bg-blue-100 px-1 py-0.2 rounded">
                                      You
                                    </span>
                                  )}
                                </div>
                                <p className="text-[11px] text-gray-400 truncate">
                                  {m.email || "Classmate"}
                                </p>
                              </div>
                            </div>

                            {m.isOwner ? (
                              <span className="text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-md shrink-0">
                                👑 Admin
                              </span>
                            ) : (
                              <span className="text-[10px] font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md shrink-0">
                                Peer
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>

        {/* ========================================================================= */}
        {/* MODAL FOOTER */}
        {/* ========================================================================= */}
        <div className="px-5 sm:px-7 py-3.5 bg-white border-t border-gray-100 flex items-center justify-between gap-3 shrink-0">
          <p className="text-xs text-gray-400 hidden sm:block">
            Showing <strong className="text-gray-700 font-bold">{filteredMemberships.length}</strong> of{" "}
            <strong className="text-gray-700 font-bold">{data.totalMembers}</strong> total member seats
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-5 py-2.5 rounded-xl font-bold text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
          >
            Close
          </button>
        </div>

      </div>
    </div>
  );
};

export default StudyBuddiesModal;
